import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { toast } from "@/components/podkit/toasts/use-toast";
import { TierViolationContext } from "@/components/QueryClientProvider";
import { TierUpgradeTile } from "@/components/tiering/TierUpgradeTile";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { CHANGE_PLAN_QUERY_KEY } from "@/routes/settings/organization/billing/Plan";
import { formatError } from "@/utils/errors";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { PlusIcon } from "lucide-react";
import { useCallback, useContext, useMemo, type FC } from "react";

export const CORE_WAITLIST_JOINED = "CORE_WAITLIST_JOINED";
export const ENTERPRISE_TRIAL_REQUESTED = "ENTERPRISE_TRIAL_REQUESTED";

export const TierViolationModal: FC = () => {
    const { isTierViolated, setTierViolation, message, onCloseCallback, clearCallback, requiredTier } =
        useContext(TierViolationContext);

    const segmentTrack = useSegmentTrack();
    const { value: waitlistJoinedRaw, setValueAsync: setWaitlistJoined } = usePreferenceValue(CORE_WAITLIST_JOINED);
    const { value: enterpriseTrialRequestedRaw, setValueAsync: setEnterpriseTrialRequested } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    // preferences are just strings, so we need to convert them to booleans
    const waitlistJoined = waitlistJoinedRaw === "true";
    const enterpriseTrialRequested = enterpriseTrialRequestedRaw === "true";

    const { data: organization } = useOrganization();
    const { data: account } = useGetAccount();

    const tier = organization?.tier;
    const coreOrFreeOna = tier === OrganizationTier.CORE || tier === OrganizationTier.FREE_ONA;
    const onJoinCoreWaitlist = useCallback(async () => {
        try {
            segmentTrack("waitlist_joined", {
                feature: "Gitpod Core (pricing)",
                provider: "core",
                type: "plan",
                email: account?.email,
            });
            await setWaitlistJoined("true");
            toast({
                title: "You're on the waitlist!",
                description: "We will let you know once the Core tier is available.",
            });
        } catch (error) {
            toast({
                title: "Failed to join waitlist",
                description: formatError(error),
            });
        }
    }, [segmentTrack, setWaitlistJoined, account?.email]);
    const coreTierLink = useMemo(() => {
        if (tier === OrganizationTier.FREE_ONA) {
            return `/settings/billing?${CHANGE_PLAN_QUERY_KEY}=true`;
        }
        if (tier === OrganizationTier.CORE) {
            return `/settings/billing`;
        }

        return undefined;
    }, [tier]);

    const onEnterpriseTrialRequest = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setEnterpriseTrialRequested("true");
            toast({
                title: "Trial requested!",
                description: "Our team is working on your request and will be in touch soon.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: formatError(error),
            });
        }
    }, [segmentTrack, setEnterpriseTrialRequested, account?.email]);

    const handleVisibilityChange = useCallback(
        (open: boolean) => {
            if (!open) {
                setTierViolation(false);
                if (onCloseCallback) {
                    onCloseCallback();
                    clearCallback();
                }
            }
        },
        [setTierViolation, onCloseCallback, clearCallback],
    );

    const coreLabel = useMemo(() => {
        if (organization?.tier === OrganizationTier.FREE_ONA) {
            return "Upgrade to Core";
        }
        if (organization?.tier === OrganizationTier.CORE) {
            return "Go to Billing";
        }

        if (waitlistJoined) {
            return "You're on the Waitlist";
        }
        return "Join waitlist";
    }, [organization?.tier, waitlistJoined]);
    const enterpriseLabel = enterpriseTrialRequested ? "You've requested a trial" : "Request Free Trial";

    return (
        <Dialog open={isTierViolated} onOpenChange={handleVisibilityChange}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.TierViolationModal}>
                <DialogHeader>
                    <DialogTitle>Access all Ona features</DialogTitle>
                </DialogHeader>
                <DialogDescription className="mb-5 text-content-primary first-letter:uppercase">
                    <span>{message || "This feature is not available on your current plan"}</span>
                    <span>
                        . Please upgrade to{" "}
                        <span>{requiredTier === OrganizationTier.CORE ? "Core" : "Enterprise"}</span> or{" "}
                        <a href="https://ona.com/contact/sales" target="_blank" rel="noreferrer" className="underline">
                            contact sales
                        </a>
                        .
                    </span>
                </DialogDescription>
                <DialogBody className="pb-6 pt-8">
                    <div className="flex flex-col gap-3">
                        <TierUpgradeTile
                            title="Enterprise"
                            description="Maximum security, compliance, and full control"
                            action={enterpriseLabel}
                            onClick={onEnterpriseTrialRequest}
                            disabled={enterpriseTrialRequested}
                            data-tracking-id="request-enterprise-trial-tier-violation-dialog"
                        />

                        <TierUpgradeTile
                            title="Core"
                            description="Ideal for individual users and teams"
                            action={coreLabel}
                            onClick={!coreOrFreeOna ? onJoinCoreWaitlist : undefined}
                            to={coreOrFreeOna ? coreTierLink : undefined}
                            disabled={waitlistJoined && !coreOrFreeOna}
                            data-tracking-id="upgrade-tier-tier-violation-dialog"
                        />
                    </div>

                    <div className="mt-8 flex flex-col gap-2">
                        <Button className="flex gap-1" variant="primary" asChild>
                            <a href="https://ona.com/pricing#pricing-comparison" target="_blank" rel="noreferrer">
                                <PlusIcon className="size-4" />
                                Compare features
                            </a>
                        </Button>
                        <Button
                            onClick={() => handleVisibilityChange(false)}
                            variant="text"
                            className="text-base font-normal text-content-primary"
                            data-tracking-id="dismiss-tier-violation-dialog"
                        >
                            Not now
                        </Button>
                    </div>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};
